import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import type {
  AdminPartnerCategoryAutoAssignResponse,
  AdminPartnerCategoryGroup,
  AdminPartnerCategoryTag,
  CreateAdminPartnerCategoryGroupRequest,
  SetAdminPartnerCategoryGroupTagsRequest,
  UpdateAdminPartnerCategoryGroupRequest,
} from "@/types/admin-partner-category-group";

// DV-159 — Partners → Categories. Every change moves tags between the groups
// and "Other", so each mutation refreshes both lists.
export const partnerCategoryGroupKeys = {
  all: ["partnerCategoryGroups"] as const,
  groups: () => [...partnerCategoryGroupKeys.all, "groups"] as const,
  unassignedTags: () =>
    [...partnerCategoryGroupKeys.all, "unassignedTags"] as const,
};

export const usePartnerCategoryGroups = () =>
  useQuery<AdminPartnerCategoryGroup[]>({
    queryKey: partnerCategoryGroupKeys.groups(),
    queryFn: () => adminPartnersService.listCategoryGroups(),
  });

export const useUnassignedPartnerCategoryTags = () =>
  useQuery<AdminPartnerCategoryTag[]>({
    queryKey: partnerCategoryGroupKeys.unassignedTags(),
    queryFn: () => adminPartnersService.listUnassignedCategoryTags(),
  });

const useRefreshCategoryGroups = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: partnerCategoryGroupKeys.all });
};

export const useCreatePartnerCategoryGroup = () => {
  const refresh = useRefreshCategoryGroups();
  return useMutation<
    AdminPartnerCategoryGroup,
    unknown,
    CreateAdminPartnerCategoryGroupRequest
  >({
    mutationFn: (data) => adminPartnersService.createCategoryGroup(data),
    onSettled: refresh,
  });
};

export const useUpdatePartnerCategoryGroup = () => {
  const refresh = useRefreshCategoryGroups();
  return useMutation<
    AdminPartnerCategoryGroup,
    unknown,
    { id: string; data: UpdateAdminPartnerCategoryGroupRequest }
  >({
    mutationFn: ({ id, data }) =>
      adminPartnersService.updateCategoryGroup(id, data),
    onSettled: refresh,
  });
};

export const useSetPartnerCategoryGroupTags = () => {
  const refresh = useRefreshCategoryGroups();
  return useMutation<
    AdminPartnerCategoryGroup,
    unknown,
    { id: string; data: SetAdminPartnerCategoryGroupTagsRequest }
  >({
    mutationFn: ({ id, data }) =>
      adminPartnersService.setCategoryGroupTags(id, data),
    onSettled: refresh,
  });
};

export const useDeletePartnerCategoryGroup = () => {
  const refresh = useRefreshCategoryGroups();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => adminPartnersService.deleteCategoryGroup(id),
    onSettled: refresh,
  });
};

export const useAutoAssignPartnerCategoryTags = () => {
  const refresh = useRefreshCategoryGroups();
  return useMutation<AdminPartnerCategoryAutoAssignResponse, unknown, void>({
    mutationFn: () => adminPartnersService.autoAssignCategoryTags(),
    onSettled: refresh,
  });
};
